import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import PageLayout from '../../../Containers/PageLayout';
import HeadingRow from '../components/HeadingRow';
import { Grid, IconButton } from '@mui/material';
import './orderSetting.scss';
import Images from '../../../Utils/images';
import CustomButton from '../../../Components/CustomButton';
import { ROUTES } from '../../../helpers/contants';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useListing } from '../../../hooks/useListing';
import endPoints from '../../../api/endpoint';
import NoData from '../../../Components/Nodata';
import {
  approvedContacts,
  getOrderSettingData,
} from '../../../redux/dashboardSlice';
import { useAppSelector } from '../../../redux/store';
import Loading from '../../../Components/Loading';
import InfiniteScroll from '../../../Components/InfiniteScrollComponent';
import AddApproverLevelModal from './components/AddApproverLevelModal';
import EditApproverLevelModal from './components/EditApproverLevelModal';
import { getContactlistDept } from '../../../redux/ProductionSlice';
import { setFilterApplied } from '../../../redux/commonSlice';
import Filter from './components/Filter';
import { isFromManageEntity } from '../../../Utils/manageEntityMember';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { ANALYTICS_EVENTS } from '../../../helpers/analyticsEvents';

const OrderSettings = (): JSX.Element => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const productionId = searchParams.get('productionId');
  const productionName = searchParams.get('productionName');
  const fromManageEntity = isFromManageEntity(searchParams);

  const { track } = useAnalytics();
  const [loadingState, setLoadingState] = useState(false);
  const [selectedApprovers, setSelectedApprovers] = useState<string[]>([]);
  const [hasSelectionChanged, setHasSelectionChanged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openApproverLevelModal, setOpenApproverLevelModal] = useState(false);
  const [editingLevel, setEditingLevel] = useState<{
    index?: number;
    levelName?: string;
  } | null>(null);
  const [searchKey, setSearchKey] = useState<string>('');
  const [departmentFilterKey, setDepartmentFilterKey] = useState(0);
  const [filter, setFilter] = useState<any>({
    page: 1,
    limit: 300,
    searchKey: '',
    productionId: productionId,
    roleId: '',
  });

  const { fetchListing, data, hasMore, loading, setData, setLoading } =
    useListing<any>({
      url: endPoints.dashBoard.orderApprovers,
      params: { ...filter },
      isInfinite: true,
      isSet: true,
    });
  const { approvers } = useAppSelector(state => state.dashboard);
  const { selectedEntity } = useAppSelector(state => state.common);
  const { contactlistdept } = useAppSelector(state => state.production);
  const productionBreadcrumbTitle =
    productionName || selectedEntity?.name || 'Production';

  const productionOwner =
    contactlistdept?.userPermissions?.['Production Owners']?.[0];
  const productionOwnerFirstName = productionOwner?.firstName ?? '';
  const productionOwnerLastName = productionOwner?.lastName ?? '';

  const activeTab = searchParams.get('tab') || '';
  const getRoleIdByTab = (tabName: string): string => {
    const roleId = '';
    if (!approvers) return roleId;
    for (const level of approvers) {
      const found = level.roles?.find((role: any) => role.roleName === tabName);
      if (found) return found._id;
    }
    return roleId;
  };

  const setTab = (tabName: string): void => {
    track(ANALYTICS_EVENTS.ORDER_SETTINGS_APPROVAL_LEVEL_SELECTED, {
      level_name: tabName,
    });
    setSearchParams((prev: URLSearchParams) => {
      const updated = new URLSearchParams(prev);
      if (!tabName) {
        updated.delete('tab');
      } else {
        updated.set('tab', tabName);
      }
      return updated;
    });
  };

  const resetSearchAndDepartmentFilter = useCallback(
    (nextRoleId?: string): void => {
      setSearchKey('');
      setFilter((prev: any) => ({
        ...prev,
        ...(nextRoleId !== undefined ? { roleId: nextRoleId } : {}),
        page: 1,
        searchKey: '',
        departmentIds: undefined,
        sortBy: undefined,
      }));
      setDepartmentFilterKey(k => k + 1);
      dispatch(setFilterApplied(false));
    },
    [dispatch],
  );

  useEffect(() => {
    track(ANALYTICS_EVENTS.ORDER_SETTINGS_PAGE_VIEW, {
      production_id: productionId ?? undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset selection state when switching between tabs
  useEffect(() => {
    setSelectedApprovers([]);
    setHasSelectionChanged(false);
  }, [activeTab]);

  useEffect(() => {
    if (!approvers || approvers?.length === 0 || !activeTab) return;
    resetSearchAndDepartmentFilter(getRoleIdByTab(activeTab));
  }, [activeTab, approvers, resetSearchAndDepartmentFilter]);
  useEffect(() => {
    setLoadingState(true);
    getOrderSettingData(productionId, () => {
      setLoadingState(false);
    });

    getContactlistDept('productions', productionId ?? '', '', '', 'CONTACT');
  }, [productionId]);

  useEffect(() => {
    if (!filter.roleId) {
      if (filter.page === 1) setData([]);
      setLoading(false);
      return;
    }
    if (filter.page === 1) {
      setData([]);
    }
    fetchListing();
  }, [filter]);

  useEffect(() => {
    // Do not override user changes when new pages load
    if (hasSelectionChanged || !data || data.length === 0) return;

    const initialSelected: string[] = [];

    if (activeTab.includes('Department')) {
      data.forEach((dept: any) => {
        dept.approvers?.forEach((val: any) => {
          if (val.isAlreadyAdded) {
            initialSelected.push(val.approverUserId);
          }
        });
      });
    } else {
      data.forEach((item: any) => {
        if (item.isAlreadyAdded) {
          initialSelected.push(item.approverUserId);
        }
      });
    }

    const isSearchActive = Boolean(filter.searchKey?.trim());
    const isDepartmentFilterActive =
      activeTab.includes('Department') &&
      Array.isArray(filter.departmentIds) &&
      filter.departmentIds.length > 0;

    const shouldMergePartialView = isSearchActive || isDepartmentFilterActive;

    if (shouldMergePartialView) {
      const visibleApproverIds = new Set<string>();
      if (activeTab.includes('Department')) {
        data.forEach((dept: any) => {
          dept.approvers?.forEach((val: any) => {
            if (val.approverUserId) visibleApproverIds.add(val.approverUserId);
          });
        });
      } else {
        data.forEach((item: any) => {
          if (item.approverUserId) visibleApproverIds.add(item.approverUserId);
        });
      }

      setSelectedApprovers((prev: string[]) => {
        const kept = prev.filter((id: string) => !visibleApproverIds.has(id));
        return Array.from(new Set([...kept, ...initialSelected]));
      });
      return;
    }

    setSelectedApprovers(initialSelected);
  }, [
    data,
    activeTab,
    hasSelectionChanged,
    filter.searchKey,
    filter.departmentIds,
  ]);
  const loadMore = (): void => {
    if (!loading && hasMore) {
      setFilter({
        ...filter,
        page: filter.page + 1,
      });
    }
  };

  // Wrapper so Filter component never overwrites productionId/roleId (e.g. on reset)
  const setFilterForOrderApprovers = (newFilterOrUpdater: any): void => {
    setFilter((prev: any) => {
      const next =
        typeof newFilterOrUpdater === 'function'
          ? newFilterOrUpdater(prev)
          : newFilterOrUpdater;
      return {
        ...next,
        productionId:
          next.productionId ?? prev.productionId ?? productionId ?? undefined,
        roleId: next.roleId ?? prev.roleId,
      };
    });
  };

  const handleCancel = (): void => {
    if (!data || data.length === 0) return;

    const resetSelected: string[] = [];

    if (activeTab.includes('Department')) {
      data.forEach((dept: any) => {
        dept.approvers?.forEach((val: any) => {
          if (val.isAlreadyAdded) {
            resetSelected.push(val.approverUserId);
          }
        });
      });
    } else {
      data.forEach((item: any) => {
        if (item.isAlreadyAdded) {
          resetSelected.push(item.approverUserId);
        }
      });
    }

    setSelectedApprovers(resetSelected);
    setHasSelectionChanged(false);
  };
  const handleSubmit = async (): Promise<void> => {
    setIsSubmitting(true);
    const payload = {
      roleId: filter.roleId,
      productionId: productionId,
      approverUserId: selectedApprovers,
    };
    approvedContacts(payload, () => {
      track(ANALYTICS_EVENTS.ORDER_SETTINGS_APPROVERS_SUBMITTED, {
        production_id: productionId ?? undefined,
        role_id: filter.roleId ?? undefined,
      });
      getOrderSettingData(productionId);
      resetSearchAndDepartmentFilter();
      setHasSelectionChanged(false);
      setIsSubmitting(false);
    });
  };

  return (
    <PageLayout>
      {loadingState ? (
        <Loading type="div" height="full" />
      ) : (
        <div className="order-settings">
          <HeadingRow
            title="Order Settings"
            isOrderSetting
            handleClearSearch={(): void => {
              setSearchKey('');
              setFilter((prev: any) => ({
                ...prev,
                page: 1,
                searchKey: '',
              }));
            }}
            searchKey={searchKey}
            setSearchKey={setSearchKey}
            isDisabled={approvers && approvers.length >= 6}
            productionId={productionId ?? ''}
            isManage
            isShowBreadcrumb
            isSearch
            onSearch={(val: string): void => {
              setFilter((prev: any) => ({
                ...prev,
                page: 1,
                limit: 300,
                searchKey: val,
              }));
            }}
            showBreadCrumb={(): React.ReactNode => {
              const parentPath =
                fromManageEntity && productionId
                  ? `${ROUTES.MANAGE_STUDIO}/${productionId}?entityType=production`
                  : productionId
                  ? `${ROUTES.PRODUCTION_DETAIL}/${productionId}`
                  : '#';

              return (
                <ul className="breadCrumbs" style={{ cursor: 'pointer' }}>
                  <li>
                    <a
                      className="cursor-pointer"
                      href={parentPath}
                      onClick={(
                        e: React.MouseEvent<HTMLAnchorElement>,
                      ): void => {
                        e.preventDefault();
                        if (productionId) {
                          navigate(parentPath);
                        }
                      }}
                    >
                      {productionBreadcrumbTitle}
                    </a>
                  </li>

                  <li>Order Settings</li>
                </ul>
              );
            }}
            onAddApproverLevel={(): void => {
              track(
                ANALYTICS_EVENTS.ORDER_SETTINGS_APPROVER_LEVEL_MODAL_OPENED,
                {},
              );
              setOpenApproverLevelModal(true);
            }}
            onAddNewMemberFromOrderSettings={(): void => {
              track(ANALYTICS_EVENTS.ORDER_SETTINGS_ADD_MEMBER_CLICKED, {});
              navigate(
                `${ROUTES.ADD_CONTACT_ROLES_PRODUCTION}?productionId=${productionId}&type=CONTACT`,
              );
            }}
          />

          <Grid container spacing={0} className="align-unset">
            <Grid item lg={5} xs={12}>
              <div className="left-listing order-approval-list with-buttons">
                <h3 className="MuiTypography-body1 mb-8 fw600">
                  Order Approval
                </h3>
                <p className="MuiTypography-body2 mb-24 text-text lh-14">
                  Default order approval is set by role type, following these
                  steps <br /> (orders move through approval from top to bottom)
                </p>

                <div className="order-approval-list-body">
                  <div>
                    {approvers
                      ?.slice(0, approvers.length - 1)
                      .map((item: any, index: number) => (
                        <>
                          <div className="d-flex align-center flex-between">
                            <p className="MuiTypography-body2 fw500">
                              {item.levelName}
                            </p>
                            {index >= 2 ? (
                              <IconButton
                                size="small"
                                aria-label={`Edit ${item.levelName}`}
                                onClick={(): void => {
                                  setEditingLevel({
                                    index,
                                    levelName: item.roles?.[0]?.roleName,
                                  });
                                }}
                              >
                                <img src={Images.EDIT_BLACK} alt="Edit" />
                              </IconButton>
                            ) : null}
                          </div>

                          {/* <hr className="light mb-0 mt-16" /> */}

                          {item.roles?.map((val: any) => (
                            <>
                              <button
                                className={`order-level-btn ${
                                  activeTab === val.roleName ? 'active' : ''
                                }`}
                                onClick={(): void => {
                                  setTab(val.roleName);
                                }}
                              >
                                <span>
                                  {index === 0
                                    ? 'Department Level'
                                    : index === 1
                                    ? 'Production Level'
                                    : val.roleName}
                                </span>{' '}
                                <span className="align-center d-flex">
                                  {val.roleName.includes('Department') ? (
                                    <>
                                      <b className="fw500 text-gray_900">
                                        {val.addedApprovers} /{' '}
                                        {val.allDepartments}
                                      </b>
                                    </>
                                  ) : (
                                    <>
                                      <b className="fw500 text-gray_900">
                                        {val.addedApprovers}{' '}
                                        {`${
                                          val.totalApprovers > 0
                                            ? `/ ${val.totalApprovers}`
                                            : ''
                                        }`}
                                      </b>
                                    </>
                                  )}
                                  <i className="arrow-right-icon ml-4"></i>
                                </span>
                              </button>
                            </>
                          ))}

                          {/* {index < approvers.length - 2 && (
                        <hr className="dashed mb-16 mt-16" />
                      )} */}
                        </>
                      ))}
                  </div>

                  <hr className="divider-bottom mb-16 mt-16" />

                  <h3 className="MuiTypography-body1 mb-8 fw600">
                    {approvers && approvers[approvers.length - 1]?.levelName}
                  </h3>
                  <p className="MuiTypography-body2 mb-24 text-text lh-14">
                    Default order approval is set by role type as follows
                  </p>

                  <p className="MuiTypography-body2 fw500">Payment Approver</p>

                  {/* <hr className="light mb-16 mt-16" /> */}
                  {approvers &&
                    approvers[approvers.length - 1]?.roles?.map((val: any) => (
                      <>
                        <button
                          className={`order-level-btn ${
                            activeTab === val.roleName ? 'active' : ''
                          }`}
                          onClick={(): void => {
                            setTab(val.roleName);
                          }}
                        >
                          <span>{val.roleName}</span>{' '}
                          <span className="align-center d-flex">
                            <b className="fw500 text-gray_900">
                              {val.addedPayers}{' '}
                              {`${
                                val.totalPayers > 0
                                  ? `/ ${val.totalPayers}`
                                  : ''
                              }`}
                            </b>{' '}
                            <i className="arrow-right-icon ml-4"></i>
                          </span>
                        </button>
                        {/* <hr className="light mb-16 mt-16" /> */}
                      </>
                    ))}
                </div>
              </div>
            </Grid>

            {!activeTab ? (
              <Grid
                item
                lg={7}
                xs={12}
                className="d-flex align-center justify-center"
              >
                <NoData
                  heading="List of approvers will be displayed here."
                  title="Tap on a list to see the full list of members here."
                />
              </Grid>
            ) : (
              <Grid item lg={7} xs={12}>
                <div className="order-approval-list with-buttons">
                  <div className="d-flex flex-between align-center mb-24">
                    <div className="pr-15">
                      <h2 className="MuiTypography-body2 fw600 lh-16 text-gray_900">
                        <span className="MuiTypography-body1 fw600 lh-16 text-gray_900">
                          {activeTab}
                        </span>
                      </h2>
                      <p className="MuiTypography-subtitle1 text-text mt-4">
                        If no approver is added or assigned, the default
                        approver will be the{' '}
                        <b className="fw600 text-gray_900">Production Owner</b>{' '}
                        as the approver and payer.
                      </p>
                    </div>

                    {activeTab.includes('Department') ? (
                      <Filter
                        key={departmentFilterKey}
                        filter={filter}
                        setFilter={setFilterForOrderApprovers}
                        productionId={productionId}
                      />
                    ) : null}
                  </div>

                  <div className="order-approval-list-body">
                    {!activeTab.includes('Department') && (
                      <>
                        <div className="grid-card border mb-16">
                          <img
                            src={
                              productionOwner?.profilePic ??
                              Images.Image_Placegolder
                            }
                            alt="production owner"
                          />
                          <div className="text">
                            <div>
                              <h2 className="MuiTypography-body2 fw600 lh-16 text-gray_900 mb-6">
                                {productionOwnerFirstName}{' '}
                                {productionOwnerLastName}
                              </h2>
                              <div className="chips">
                                <span className="blue">Production Owner</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {!loading && data.length === 0 && (
                          <div className="grey-box mb-16 text-center border">
                            <p className="MuiTypography-subtitle1 text-text mb-16">
                              No member is added at the production level. <br />
                              Please add a member for order approval.
                            </p>
                            <CustomButton
                              size="medium"
                              variant="contained"
                              text="+ Add Member"
                              showIcon={false}
                              width="auto"
                              onClick={(): void =>
                                navigate(
                                  `${ROUTES.ADD_CONTACT_ROLES_PRODUCTION}?productionId=${productionId}&type=CONTACT`,
                                )
                              }
                              id="signup"
                              className="sm"
                            />
                          </div>
                        )}
                      </>
                    )}

                    {
                      <InfiniteScroll
                        isLoading={loading}
                        noData={
                          !loading &&
                          !data.length &&
                          !activeTab.includes('Production') &&
                          !activeTab.includes('Payer')
                        }
                        hasMore={hasMore}
                        onLoadMore={loadMore}
                        className="with-title"
                        isOrderSetting
                        noDataText={`No ${activeTab} found.`}
                        title={`We're sorry, but we couldn't find your ${activeTab} `}
                      >
                        {data?.map((item: any) => (
                          <>
                            {activeTab.includes('Department') ? (
                              <>
                                {' '}
                                <div className="grey-heading mb-16">
                                  <h2 className="MuiTypography-body2 fw600 lh-16">
                                    {item.deptName}
                                  </h2>
                                </div>
                                {item.approvers?.length > 0 ? (
                                  <>
                                    {item.approvers?.map(
                                      (val: any, index: number) => (
                                        <div
                                          className="approval-col"
                                          key={index}
                                        >
                                          <div className="contact-card no-cursor">
                                            <img
                                              src={
                                                val?.profilePic ??
                                                Images.Image_Placegolder
                                              }
                                              alt={val.userName}
                                              onError={(e: any): void => {
                                                e.target.onerror = null;
                                                e.target.src =
                                                  Images.Image_Placegolder;
                                              }}
                                            />
                                            <div className="d-flex flex-between text">
                                              <div className="name">
                                                <h3 className="MuiTypography-body2 fw600 mb-6">
                                                  {val.userName}
                                                </h3>
                                                <p className="MuiTypography-body2 text-text">
                                                  {' '}
                                                  {val.desigination}
                                                </p>
                                              </div>
                                              <div className="checkbox">
                                                <input
                                                  type="checkbox"
                                                  checked={selectedApprovers.includes(
                                                    val.approverUserId,
                                                  )}
                                                  onChange={(
                                                    e: React.ChangeEvent<HTMLInputElement>,
                                                  ): void => {
                                                    setHasSelectionChanged(
                                                      true,
                                                    );
                                                    if (e.target.checked) {
                                                      setSelectedApprovers(
                                                        (
                                                          prev: string[],
                                                        ): string[] => [
                                                          ...prev,
                                                          val.approverUserId,
                                                        ],
                                                      );
                                                    } else {
                                                      setSelectedApprovers(
                                                        (
                                                          prev: string[],
                                                        ): string[] =>
                                                          prev.filter(
                                                            (
                                                              id: string,
                                                            ): boolean =>
                                                              id !==
                                                              val.approverUserId,
                                                          ),
                                                      );
                                                    }
                                                  }}
                                                />
                                                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                                                <label
                                                  aria-label={`Select ${val.userName}`}
                                                ></label>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ),
                                    )}
                                  </>
                                ) : (
                                  <Grid
                                    container
                                    spacing={1}
                                    className="align-center mb-20"
                                  >
                                    <Grid item lg={9} xs={12}>
                                      <p className="MuiTypography-body2 text-text">
                                        No department head has been added for
                                        this department.
                                      </p>
                                    </Grid>
                                    <Grid
                                      item
                                      lg={3}
                                      xs={12}
                                      className="text-md-right"
                                    >
                                      <CustomButton
                                        size="medium"
                                        variant="contained"
                                        text="+ Add Member"
                                        showIcon={false}
                                        width="auto"
                                        onClick={(): void =>
                                          navigate(
                                            `${ROUTES.ADD_CONTACT_ROLES_PRODUCTION}?productionId=${productionId}&deptId=${item.deptId}&type=CONTACT`,
                                          )
                                        }
                                        id="signup"
                                        className="sm"
                                      />
                                    </Grid>
                                  </Grid>
                                )}
                              </>
                            ) : (
                              <>
                                <div className="approval-col">
                                  <div className="contact-card no-cursor">
                                    <img
                                      src={
                                        item?.profilePic ??
                                        Images.Image_Placegolder
                                      }
                                      alt={item.userName}
                                      onError={(e: any): void => {
                                        e.target.onerror = null;
                                        e.target.src = Images.Image_Placegolder;
                                      }}
                                    />
                                    <div className="d-flex flex-between text">
                                      <div className="name">
                                        <h3 className="MuiTypography-body2 fw600 mb-6">
                                          {item.userName}
                                        </h3>
                                        <p className="MuiTypography-body2 text-text">
                                          {' '}
                                          {item.desigination}
                                        </p>
                                      </div>

                                      <div className="checkbox">
                                        <input
                                          type="checkbox"
                                          checked={selectedApprovers.includes(
                                            item.approverUserId,
                                          )}
                                          onChange={(
                                            e: React.ChangeEvent<HTMLInputElement>,
                                          ): void => {
                                            setHasSelectionChanged(true);
                                            if (e.target.checked) {
                                              setSelectedApprovers(
                                                (prev: string[]): string[] => [
                                                  ...prev,
                                                  item.approverUserId,
                                                ],
                                              );
                                            } else {
                                              setSelectedApprovers(
                                                (prev: string[]): string[] =>
                                                  prev.filter(
                                                    (id: string): boolean =>
                                                      id !==
                                                      item.approverUserId,
                                                  ),
                                              );
                                            }
                                          }}
                                        />
                                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                                        <label
                                          aria-label={`Select ${item.userName}`}
                                        ></label>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            )}
                          </>
                        ))}
                      </InfiniteScroll>
                    }
                  </div>
                </div>
              </Grid>
            )}
          </Grid>
          {activeTab && data.length > 0 && (
            <div className="cart-lower-btns btn-grp">
              <div>
                <CustomButton
                  size="large"
                  variant="outlined"
                  text={'Cancel'}
                  width="auto"
                  type="button"
                  id="cancel"
                  disabled={false}
                  showIcon={false}
                  className="min_width_btn_160"
                  // onClick={handleCropFinish}
                  onClick={(): void => {
                    handleCancel();
                  }}
                />
              </div>
              <div>
                <CustomButton
                  size="large"
                  variant="contained"
                  text={'Submit'}
                  width="auto"
                  type="button"
                  disabled={
                    isSubmitting ||
                    (selectedApprovers.length === 0 && !hasSelectionChanged)
                  }
                  id="Submit"
                  showIcon={false}
                  className="min_width_btn_160"
                  onClick={(): Promise<void> => handleSubmit()}
                />
              </div>
            </div>
          )}
        </div>
      )}

      <AddApproverLevelModal
        open={openApproverLevelModal}
        onClose={(): void => setOpenApproverLevelModal(false)}
        productionId={productionId}
        levelIndex={
          approvers && approvers.length > 0 ? approvers.length - 1 : 0
        }
        onSuccess={(): void => {
          if (productionId) getOrderSettingData(productionId);
        }}
      />

      <EditApproverLevelModal
        open={!!editingLevel}
        onClose={(): void => setEditingLevel(null)}
        productionId={productionId}
        levelIndex={editingLevel?.index}
        initialLevelName={editingLevel?.levelName}
        onSuccess={(updatedLevelName?: string): void => {
          if (productionId) getOrderSettingData(productionId);
          if (
            updatedLevelName &&
            activeTab === editingLevel?.levelName &&
            updatedLevelName !== editingLevel?.levelName
          ) {
            setTab(updatedLevelName);
          }
        }}
      />

      {/* <AddRole open={open} /> */}
    </PageLayout>
  );
};

export default OrderSettings;
